
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category: string;
  stock: number;
  custom_talent_reward: number | null;
  talent_reward_type: 'percentage' | 'fixed';
  seller_id?: string;
  profiles?: {
    full_name: string;
  };
}

export default function CommercePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    category: '성경/도서',
    stock: 0,
    image_url: '',
    custom_talent_reward: 0,
    talent_reward_type: 'percentage' as 'percentage' | 'fixed'
  });

  const categories = ['전체', '성경/도서', '찬양/음반', '악세서리', '문구/용품', '의류', '기타'];

  useEffect(() => {
    fetchProducts();
  }, [user]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          profiles (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('상품 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterProduct = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (!productForm.name || !productForm.description || productForm.price <= 0) {
      alert('모든 필수 항목을 입력해주세요.');
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .insert({
          name: productForm.name,
          description: productForm.description,
          price: productForm.price,
          category: productForm.category,
          stock: productForm.stock,
          image_url: productForm.image_url,
          custom_talent_reward: productForm.custom_talent_reward || null,
          talent_reward_type: productForm.talent_reward_type,
          seller_id: user.id
        });

      if (error) throw error;

      alert('상품이 등록되었습니다!');
      setShowRegisterModal(false);
      setProductForm({
        name: '',
        description: '',
        price: 0,
        category: '성경/도서',
        stock: 0,
        image_url: '',
        custom_talent_reward: 0,
        talent_reward_type: 'percentage'
      });
      fetchProducts();
    } catch (error) {
      console.error('상품 등록 실패:', error);
      alert('상품 등록 중 오류가 발생했습니다.');
    }
  };

  const calculateTalentReward = (product: Product, finalPrice: number) => {
    if (product.custom_talent_reward) {
      if (product.talent_reward_type === 'fixed') {
        return product.custom_talent_reward;
      } else {
        return Math.floor(finalPrice * (product.custom_talent_reward / 100));
      }
    }
    return Math.floor(finalPrice * 0.01);
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === '전체' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">커머스</h1>
            <p className="text-lg text-gray-600">신앙 생활에 필요한 다양한 상품을 만나보세요</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                  <input
                    type="text"
                    placeholder="상품 검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  />
                </div>
              </div>
              {user && (
                <button 
                  onClick={() => setShowRegisterModal(true)}
                  className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  상품 등록
                </button>
              )}
              <button className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-shopping-cart-line mr-2"></i>
                장바구니
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              등록된 상품이 없습니다.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => navigate(`/commerce/product/${product.id}`)}
                  className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative w-full h-72 overflow-hidden bg-gray-50">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-6xl text-gray-300"></i>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    {product.profiles?.full_name && (
                      <p className="text-xs text-gray-500 mb-2">{product.profiles.full_name}</p>
                    )}
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xl font-bold text-gray-900">
                          {product.price.toLocaleString()}원
                        </p>
                        <div className="flex items-center space-x-1 text-amber-600 mt-1">
                          <i className="ri-coin-line text-sm"></i>
                          <span className="text-sm font-semibold">
                            {calculateTalentReward(product, product.price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <button className="w-10 h-10 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center cursor-pointer">
                        <i className="ri-shopping-cart-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 상품 등록 모달 */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">상품 등록</h2>
              <button
                onClick={() => setShowRegisterModal(false)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleRegisterProduct} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품명 *
                </label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="상품명을 입력하세요"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  카테고리 *
                </label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  {categories.filter(c => c !== '전체').map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  상품 설명 *
                </label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                  placeholder="상품 설명을 입력하세요"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    가격 (원) *
                  </label>
                  <input
                    type="number"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    재고 수량 *
                  </label>
                  <input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="0"
                    min="0"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  이미지 URL
                </label>
                <input
                  type="url"
                  value={productForm.image_url}
                  onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">상품 이미지 URL을 입력하세요</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  달란트 적립 설정
                </label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="reward_type"
                        value="percentage"
                        checked={productForm.talent_reward_type === 'percentage'}
                        onChange={(e) => setProductForm({ ...productForm, talent_reward_type: e.target.value as 'percentage' | 'fixed' })}
                        className="mr-2 cursor-pointer"
                      />
                      <span>비율 (%)</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="reward_type"
                        value="fixed"
                        checked={productForm.talent_reward_type === 'fixed'}
                        onChange={(e) => setProductForm({ ...productForm, talent_reward_type: e.target.value as 'percentage' | 'fixed' })}
                        className="mr-2 cursor-pointer"
                      />
                      <span>고정 금액</span>
                    </label>
                  </div>
                  <input
                    type="number"
                    value={productForm.custom_talent_reward}
                    onChange={(e) => setProductForm({ ...productForm, custom_talent_reward: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder={productForm.talent_reward_type === 'percentage' ? '예: 5 (5%)' : '예: 1000 (1000 달란트)'}
                    min="0"
                  />
                  <p className="text-xs text-gray-500">
                    {productForm.talent_reward_type === 'percentage' 
                      ? '구매 금액의 몇 %를 적립할지 입력하세요 (0 입력 시 기본 1% 적립)'
                      : '구매 시 적립할 고정 달란트를 입력하세요 (0 입력 시 기본 1% 적립)'}
                  </p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  등록하기
                </button>
                <button
                  type="button"
                  onClick={() => setShowRegisterModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
